<template>
  <Container class="overviewContainer">
    <Grid hasGap>
      <GridItem :gridSpan="12">
        <PasswordManagerGetStartedBanner
          @showGroupModal="handleShowGroupModal"
        />
      </GridItem>
      <GridItem :gridSpan="12">
        <UpgradeNudge
          v-if="$hasFeatureFlag($featureFlags.passwordManagerTrial)"
          :data-test-id="$testId('upgradeNudge')"
        />
      </GridItem>
      <GridItem :gridSpan="4">
        <CardStatus
          :data-test-id="$testId('pendingInvitesCardStatus')"
          type="warning"
        >
          <template v-slot:header>
            <div>
              <HeadingMedium :hasMargin="false">
                {{ overviewData.pendingInvites }}
              </HeadingMedium>
              <HeadingExtraSmall>Pending Invites</HeadingExtraSmall>
            </div>
          </template>
          <template v-slot:body>
            <div>
              <Button
                :data-test-id="$testId('allUsersButton')"
                hasOutline
                text="View All"
                type="secondary"
                @click="handleAllUsersClick"
              />
            </div>
          </template>
        </CardStatus>
      </GridItem>
      <GridItem :gridSpan="4">
        <CardStatus
          :data-test-id="$testId('sharedFoldersCardStatus')"
          type="informational"
        >
          <template v-slot:header>
            <div>
              <HeadingMedium :hasMargin="false">
                {{ overviewData.sharedFolders }}
              </HeadingMedium>
              <HeadingExtraSmall>Shared Folders</HeadingExtraSmall>
            </div>
          </template>
          <template v-slot:body>
            <div>
              <Button
                :data-test-id="$testId('sharedFoldersButton')"
                hasOutline
                text="View All"
                type="secondary"
                @click="handleViewSharedFoldersClick"
              />
            </div>
          </template>
        </CardStatus>
      </GridItem>
      <GridItem :gridSpan="4">
        <CardStatus
          :data-test-id="$testId('enrolledGroupsCardStatus')"
          type="informational"
        >
          <template v-slot:header>
            <div>
              <HeadingMedium :hasMargin="false">
                {{ overviewData.enrolledGroups }}
              </HeadingMedium>
              <HeadingExtraSmall>Enrolled Groups</HeadingExtraSmall>
            </div>
          </template>
          <template v-slot:body>
            <div>
              <Button
                :data-test-id="$testId('groupsButton')"
                hasOutline
                text="View All"
                type="secondary"
                @click="handleShowGroupModal"
              />
            </div>
          </template>
        </CardStatus>
      </GridItem>
      <GridItem :gridSpan="4">
        <CardStatus
          :data-test-id="$testId('passwordHealthCardStatus')"
          :type="passwordHealthStatusType"
        >
          <template v-slot:header>
            <div>
              <HeadingMedium :hasMargin="false">
                {{ overviewData.passwordsScore || '0' }}<span class="overHundred">/100</span>
              </HeadingMedium>
              <HeadingExtraSmall>Password Health</HeadingExtraSmall>
            </div>
          </template>
          <template v-slot:body>
            <div>
              <PasswordManagerPasswordScoreStatus
                class="passwordScoreStatus"
                :hasPasswords="!!overviewData.passwordsCount"
                :passwordScore="overviewData.passwordsScore"
              />
            </div>
          </template>
        </CardStatus>
      </GridItem>
      <GridItem :gridSpan="8">
        <PasswordManagerPasswordHealth
          class="passwordManagerPasswordHealth"
          :oldPasswords="overviewData.oldPasswords"
          :totalCount="overviewData.passwordsCount"
          :weakPasswords="overviewData.weakPasswords"
          @cardClick="handlePasswordHealthCardClick"
        />
      </GridItem>
      <GridItem :gridSpan="4">
        <Card
          class="devicesChartCard"
        >
          <HeadingMedium>
            Devices
          </HeadingMedium>
          <div
            class="devicesChartContainer"
          >
            <ChartDonut
              v-if="showDevicesChart"
              :colors="$options.chartDonutOptions.colors"
              :labels="$options.chartDonutOptions.labels"
              legendPosition="bottom"
              legendWidth="15rem"
              :series="devicesChartSeries"
              :showDataLabels="true"
              @selectDataPoint="handleSelectDataPoint"
            />
            <HeadingExtraSmall
              v-if="showDevicesEmptyMessage"
              class="noDevicesMessage"
              :data-test-id="$testId('noDevicesMessage')"
            >
              No devices with active Password Manager
            </HeadingExtraSmall>
          </div>
        </Card>
      </GridItem>
      <GridItem :gridSpan="8">
        <Card>
          <PasswordManagerAppVersionsTabs />
        </Card>
      </GridItem>
      <GridItem :gridSpan="12">
        <Card>
          <template
            v-slot:header
          >
            <HeadingMedium
              class="activityLogsCardHeading"
            >
              Logs
              <ButtonLink
                :data-test-id="$testId('activityLogsButton')"
                text="activity log"
                textCase="lowercase"
                @click="handleActivityLogClick"
              />
            </HeadingMedium>
          </template>
          <template
            v-slot:body
          >
            <PasswordManagerActivityLogs ref="passwordManagerActivityLogs" />
          </template>
        </Card>
      </GridItem>
    </Grid>
    <LoadingOverlay
      :showLoadingOverlay="isLoading"
    />
    <PasswordManagerUsersItemListModal
      v-if="usersModal.isVisible"
      :defaultFilters="usersModal.filter"
      :defaultSortBy="usersModal.sortBy"
      :excludedColumns="usersModal.excludedColumns"
      :headerText="usersModal.headerText"
      :selectableItemList="usersModal.selectableItemList"
      :showResendInvitationButton="usersModal.showResendInvitationButton"
      @closeUsersModal="handleCloseUsersModal"
    />
  </Container>
</template>

<script>
import {
  Button,
  ButtonLink,
  Card,
  CardStatus,
  Container,
  Grid,
  GridItem,
  HeadingExtraSmall,
  HeadingMedium,
} from '@jumpcloud/ui-components';
import { ChartDonut } from '@jumpcloud/ui-components/charts';
import { mapActions } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { passwordHealthTypes, passwordManagerOS, passwordStrengthStatus } from 'client/PasswordManager/PasswordManagerConstants';
import { columns as passwordManagerUsersItemListColumns } from 'client/PasswordManager/Users/PasswordManagerUsersItemListColumns';
import LoadingOverlay from 'client/components/LoadingOverlay/LoadingOverlay.vue';
import PasswordManagerActivityLogs from 'client/PasswordManager/components/PasswordManagerActivityLogs.vue';
import PasswordManagerAppVersionsTabs from 'client/PasswordManager/Overview/PasswordManagerAppVersionsTabs.vue';
import PasswordManagerFilters from 'client/PasswordManager/PasswordManagerFilters';
import PasswordManagerGetStartedBanner from 'client/PasswordManager/components/PasswordManagerGetStartedBanner.vue';
import PasswordManagerPasswordHealth from 'client/PasswordManager/components/PasswordManagerPasswordHealth.vue';
import PasswordManagerPasswordScoreStatus from 'client/PasswordManager/components/PasswordManagerPasswordScoreStatus.vue';
import PasswordManagerUsersItemListModal from 'client/PasswordManager/Users/PasswordManagerUsersItemListModal.vue';
import PasswordManagerUtils from 'client/PasswordManager/PasswordManagerUtils';
import UpgradeNudge from 'client/components/Nudge/UpgradeNudge.vue';
import Util from 'client/util/Util';

const PasswordManagerModule = 'PasswordManagerModule';

const hasDevices = (devices) => devices.reduce(
  (previousValue, currentValue) => previousValue + currentValue.count, 0,
);

export default {
  name: 'PasswordManagerOverview',

  components: {
    Button,
    ButtonLink,
    Card,
    CardStatus,
    ChartDonut,
    Container,
    Grid,
    GridItem,
    HeadingMedium,
    HeadingExtraSmall,
    PasswordManagerActivityLogs,
    PasswordManagerAppVersionsTabs,
    PasswordManagerGetStartedBanner,
    PasswordManagerPasswordHealth,
    PasswordManagerPasswordScoreStatus,
    PasswordManagerUsersItemListModal,
    LoadingOverlay,
    UpgradeNudge,
  },

  chartDonutOptions: {
    colors: ['jcDataVizD', 'jcDataVizE'],
    labels: ['Mobile App', 'Desktop App'],
  },

  PasswordManagerUtils,
  Util,

  data() {
    return {
      isLoading: false,
      overviewData: {},
      usersModal: {
        columns: null,
        filter: [],
        headerText: '',
        selectableItemList: false,
        showResendInvitationButton: false,
        isVisible: false,
        sortBy: 'name',
      },
    };
  },

  computed: {
    devices() {
      return this.overviewData?.devices || [];
    },

    /*
    * [mobileSeries, desktopSeries]
    */
    devicesChartSeries() {
      return this.devices.reduce(
        (previousValue, currentValue) => {
          const nextValue = [...previousValue];
          if ([
            passwordManagerOS.ios,
            passwordManagerOS.android,
          ].includes(currentValue.id)) {
            nextValue[0] += currentValue.count;
          } else {
            nextValue[1] = currentValue.count;
          }
          return nextValue;
        },
        [0, 0],
      );
    },

    passwordHealthStatusType() {
      return this.overviewData.passwordsCount ? passwordStrengthStatus[this.passwordsStrength] : 'default';
    },

    passwordsStrength() {
      return this.$options.PasswordManagerUtils
        .getPasswordStrength(this.overviewData.passwordsScore);
    },

    showDevicesChart() {
      return !this.isLoading && hasDevices(this.devices);
    },

    showDevicesEmptyMessage() {
      return !this.isLoading && !hasDevices(this.devices);
    },
  },

  created() {
    this.loadStatistics();
  },

  methods: {
    ...mapActions(PasswordManagerModule, [
      'fetchPasswordManagerStatistics',
    ]),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    handleActivityLogClick() {
      this.$refs.passwordManagerActivityLogs.redirectToDirectoryInsights();
    },

    handleAllUsersClick() {
      this.usersModal = {
        filter: [PasswordManagerFilters.activeUsers(0)],
        headerText: 'Pending Invites',
        selectableItemList: true,
        showResendInvitationButton: true,
        isVisible: true,
        excludedColumns: [
          passwordManagerUsersItemListColumns.actions,
          passwordManagerUsersItemListColumns.passwordHealth,
          passwordManagerUsersItemListColumns.weakPasswords,
          passwordManagerUsersItemListColumns.oldPasswords],
      };
    },

    handleCloseUsersModal() {
      this.usersModal = {
        filter: [],
        headerText: '',
        selectableItemList: false,
        showResendInvitationButton: false,
        isVisible: false,
        excludedColumns: [
          passwordManagerUsersItemListColumns.actions],
      };
    },

    handlePasswordHealthCardClick(passwordHealthType) {
      const isWeakPasswords = passwordHealthType === passwordHealthTypes.weakPasswords;

      this.usersModal = isWeakPasswords ? {
        filter: [
          PasswordManagerFilters.weakPasswords(0),
        ],
        headerText: 'Users with weak passwords',
        selectableItemList: false,
        showResendInvitationButton: false,
        isVisible: true,
        sortBy: '-weakPasswords',
        excludedColumns: [passwordManagerUsersItemListColumns.actions,
          passwordManagerUsersItemListColumns.oldPasswords],
      } : {
        filter: [
          PasswordManagerFilters.oldPasswords(0)],
        headerText: 'Users with old passwords',
        selectableItemList: false,
        showResendInvitationButton: false,
        isVisible: true,
        sortBy: '-oldPasswords',
        excludedColumns: [
          passwordManagerUsersItemListColumns.actions,
          passwordManagerUsersItemListColumns.weakPasswords],
      };
    },

    handleSelectDataPoint(series) {
      let usersModalHeaderText = '';
      let usersModalFilter = [];

      if (series === 0) {
        usersModalHeaderText = 'Mobile Users';
        usersModalFilter = [
          PasswordManagerFilters.operatingSystems(
            [
              passwordManagerOS.ios,
              passwordManagerOS.android,
            ],
          ),
          PasswordManagerFilters.activeUsers(1)];
      } else {
        usersModalHeaderText = 'Desktop Users';
        usersModalFilter = [
          PasswordManagerFilters.operatingSystem(
            passwordManagerOS.desktop,
          ),
          PasswordManagerFilters.activeUsers(1)];
      }

      this.usersModal = {
        filter: usersModalFilter,
        headerText: usersModalHeaderText,
        selectableItemList: false,
        showResendInvitationButton: false,
        isVisible: true,
        excludedColumns: [
          passwordManagerUsersItemListColumns.actions,
          passwordManagerUsersItemListColumns.oldPasswords,
          passwordManagerUsersItemListColumns.weakPasswords],
      };
    },

    handleShowGroupModal() {
      this.$emit('showGroupModal');
    },

    handleViewSharedFoldersClick() {
      this.$options.Util.redirect('#/passwordmanager/sharedfolders');
    },

    async loadStatistics() {
      this.isLoading = true;

      try {
        this.overviewData = (await this.fetchPasswordManagerStatistics()).data;
      } catch (e) {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'There was a problem fetching overview data. Please try again.',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.activityLogsCardHeading {
  display: flex;
  justify-content: space-between;
}

.devicesChartCard {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.devicesChartContainer {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
}

.noDevicesMessage {
  text-align: center;
}

.overHundred {
  font-size: var(--jcBodyLarge);
  font-weight: normal;
}

.overviewContainer.overviewContainer {
  --container-padding: var(--jc-spacer);

  background-color: var(--jc-background);
  min-height: 100%;
  position: relative;
}

.passwordManagerPasswordHealth {
  height: 100%;
}

.passwordScoreStatus {
  margin-top: 6px;
}
</style>
